import React, { useRef, useState, useCallback } from 'react';
import { Box, Button, Typography } from '@mui/joy';
import { styled } from '@mui/joy/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { ALLOWED_CONTENT_TYPES } from '../../helpers/storage';

const VisuallyHiddenInput = styled('input')`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  white-space: nowrap;
  width: 1px;
`;

const DropZone = styled(Box)(({ theme, isDragActive }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(25, 118, 210, 0.1)',
  border: '3px dashed',
  borderColor: theme.vars.palette.primary[500],
  zIndex: 9999,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  gap: theme.spacing(2),
  opacity: isDragActive ? 1 : 0,
  pointerEvents: isDragActive ? 'auto' : 'none',
  transition: 'opacity 0.2s ease-in-out',
}));

/**
 * Generate accept string from allowed content types
 */
const getAcceptString = () => {
  const extensions = [];
  ALLOWED_CONTENT_TYPES.forEach((type) => {
    // Map MIME types to extensions
    const mimeToExt = {
      'application/pdf': '.pdf',
      'image/png': '.png',
      'image/jpeg': '.jpeg,.jpg',
      'image/jpg': '.jpg',
      'image/gif': '.gif',
      'image/webp': '.webp',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
      'application/msword': '.doc',
      'application/vnd.ms-excel': '.xls',
      'text/plain': '.txt',
      'text/csv': '.csv',
      'application/json': '.json',
    };
    if (mimeToExt[type]) {
      extensions.push(mimeToExt[type]);
    }
  });
  return extensions.join(',');
};

const UploadButton = ({ onUpload, disabled = false }) => {
  const inputRef = useRef(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const dragCounter = useRef(0);

  const handleFiles = useCallback((files) => {
    if (files && files.length > 0) {
      onUpload(Array.from(files));
    }
  }, [onUpload]);

  const handleInputChange = (event) => {
    handleFiles(event.target.files);
    // Reset input so same file can be uploaded again
    event.target.value = '';
  };

  // Drag and drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragActive(true);
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      setIsDragActive(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    dragCounter.current = 0;

    if (disabled) return;

    const files = e.dataTransfer.files;
    handleFiles(files);
  }, [disabled, handleFiles]);

  // Attach drag handlers to window
  React.useEffect(() => {
    window.addEventListener('dragenter', handleDragEnter);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('drop', handleDrop);

    return () => {
      window.removeEventListener('dragenter', handleDragEnter);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('drop', handleDrop);
    };
  }, [handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);

  return (
    <>
      <Button
        component="label"
        variant="solid"
        color="primary"
        startDecorator={<CloudUploadIcon />}
        disabled={disabled}
      >
        Upload Files
        <VisuallyHiddenInput
          ref={inputRef}
          type="file"
          accept={getAcceptString()}
          onChange={handleInputChange}
          multiple
        />
      </Button>

      {/* Full-screen drop zone overlay */}
      <DropZone isDragActive={isDragActive}>
        <CloudUploadIcon sx={{ fontSize: 64, color: 'primary.500' }} />
        <Typography level="h4" color="primary">
          Drop files here to upload
        </Typography>
        <Typography level="body-sm" color="neutral">
          Release to start uploading
        </Typography>
      </DropZone>
    </>
  );
};

export default UploadButton;
